import React, {useEffect, useState, useMemo} from 'react';
import {useNavigate} from "react-router-dom";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    CircularProgress,
    Divider,
    Tooltip as MuiTooltip
} from "@mui/material";
import BodyMeasurementChart from "./client/BodyMeasurementChart";
import {ReactComponent as RullerIcon} from "../assets/images/ruller.svg";
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    TableChart as TableChartIcon,
    Straighten as StraightenIcon,
    Collections as CollectionsIcon,
    VideoLibrary as VideoLibraryIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    LockOutlined as LockIcon
} from "@mui/icons-material";


import {
    getLatestMeasurements,
    getLast7ByDefinition,
    deleteMeasurement
} from "../services/BodyMeasurementsService";
import AddBodyMeasurementModal from "./AddBodyMeasurementModal";
import EditMeasurementModal from "./EditMeasurementModal";
import {useMessageContext} from "../contexts/MessageContext";
import ChartCard from "./client/ChartCard";
import {RTL} from "../cache/CacheRtl";
import {colors, borders} from "./ui/colors";
import {t as coachT} from "./dashboard/coach/coachTokens";
import {imageUrl} from "../utils/imageUrl";

// --- Helpers ---

// Robust, cross-browser date parsing.
// `new Date(string)` is unreliable in Safari/Firefox: it rejects non-strict-ISO
// formats (e.g. "2026-05-19 00:00:00") and date-array shapes that Chrome's lenient
// parser happens to accept. The backend serializes dates as a LocalDate array
// ([year, month(1-based), day]), which becomes the string "2026,5,19" when stringified.
// Handle arrays, comma-separated values, and ISO strings explicitly.
const parseMeasurementDate = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const parts = Array.isArray(value)
        ? value
        : (typeof value === 'string' && value.includes(',') ? value.split(',') : null);
    if (parts && parts.length >= 3) {
        const [year, month, day] = parts.map(Number);
        if (![year, month, day].some(Number.isNaN)) {
            const date = new Date(year, month - 1, day);
            return Number.isNaN(date.getTime()) ? null : date;
        }
    }

    const str = String(value);
    const match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
        const [, year, month, day] = match;
        const date = new Date(Number(year), Number(month) - 1, Number(day));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const fallback = new Date(str);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatDate = (dateString) => {
    const date = parseMeasurementDate(dateString);
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
};

const formatFullDate = (dateString) => {
    const date = parseMeasurementDate(dateString);
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

// Unit now comes from the company measurement definition (server sends it per measurement).
// Map the stored code to its Hebrew label; fall back to ס״מ.
const UNIT_LABELS = { cm: 'ס״מ', kg: 'ק״ג', '%': '%' };
const getUnit = (measurement) => {
    const code = measurement?.unit;
    if (!code) return 'ס״מ';
    return UNIT_LABELS[code] || code;
};

// --- MeasurementSummaryRow ---

const MeasurementSummaryRow = ({measurement, name, onClick, isLast, coach = false}) => {
    const unit = getUnit(measurement);
    const reason = emptyReasonText(measurement);
    const change = measurement.value != null && measurement.previousValue != null
        ? measurement.value - measurement.previousValue
        : null;
    // down = good (green), up = red — same semantics either way
    const trendColor = change != null
        ? (change < 0 ? (coach ? coachT.green : colors.primary) : change > 0 ? (coach ? coachT.red : colors.error) : (coach ? coachT.t3 : colors.textMuted))
        : null;

    return (
        <Box
            onClick={onClick}
            sx={{
                display: "flex",
                alignItems: "center",
                padding: "10px 14px",
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                '&:hover': {
                    backgroundColor: coach ? coachT.greenLight : colors.bg
                },
                borderBottom: isLast ? "none" : `1px solid ${coach ? coachT.borderLight : colors.bgHover}`,
            }}
        >
            {/* Icon — company definition image if set, else a ruler */}
            <Box sx={{
                width: 30,
                height: 30,
                borderRadius: coach ? coachT.rxs : borders.radius.md,
                backgroundColor: coach ? coachT.bg : colors.primarySubtle,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mr: 1.5,
                overflow: "hidden"
            }}>
                {measurement.imageUrl
                    ? <img src={imageUrl(measurement.imageUrl, 30)} alt={name} style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                    : <StraightenIcon sx={{fontSize: 16, color: coach ? coachT.t2 : colors.primary}}/>}
            </Box>

            {/* Name, plus why a computed measurement is blank */}
            <Box sx={{flex: 1, minWidth: 0}}>
                <Box sx={{display: "flex", alignItems: "center", gap: "5px", minWidth: 0}}>
                    <Box sx={{fontSize: "13px", color: coach ? coachT.t2 : colors.textSecondary, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                        {name}
                    </Box>
                    {measurement.computed === true && (
                        <MuiTooltip title="מחושב אוטומטית מהיקף הבטן והצוואר — לא ניתן להזנה או לעריכה">
                            <Box sx={{
                                display: "inline-flex", alignItems: "center", gap: "2px",
                                height: 16, px: "5px", borderRadius: coach ? coachT.rxs : borders.radius.sm,
                                background: coach ? coachT.bg : colors.bgHover,
                                color: coach ? coachT.t3 : colors.textMuted,
                                fontSize: 9.5, fontWeight: 700, flexShrink: 0, whiteSpace: "nowrap",
                            }}>
                                <LockIcon sx={{fontSize: 9}}/>
                                מחושב
                            </Box>
                        </MuiTooltip>
                    )}
                </Box>
                {reason && (
                    <Box sx={{fontSize: "11px", color: coach ? coachT.t3 : colors.textMuted, lineHeight: 1.4}}>
                        {reason}
                    </Box>
                )}
            </Box>

            {/* Value + Unit */}
            <Box sx={{display: "flex", alignItems: "baseline", gap: 0.5, minWidth: "80px", justifyContent: "flex-end", mr: 2}}>
                <Box sx={{fontSize: "15px", fontWeight: 700, color: coach ? coachT.text : colors.textPrimary}}>
                    {measurement.value != null ? measurement.value.toFixed(1) : '--'}
                </Box>
                <Box sx={{fontSize: "12px", fontWeight: 500, color: coach ? coachT.t3 : colors.textMuted}}>
                    {unit}
                </Box>
            </Box>

            {/* Previous Value */}
            <Box sx={{fontSize: "12px", fontWeight: 500, color: coach ? coachT.t3 : colors.textMuted, minWidth: "55px", textAlign: "center", mr: 1.5}}>
                {measurement.previousValue != null ? measurement.previousValue.toFixed(1) : '--'}
            </Box>

            {/* Trend */}
            <Box sx={{minWidth: "65px", display: "flex", justifyContent: "flex-end"}}>
                {change !== null ? (
                    <Box sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.3,
                        backgroundColor: change < 0 ? (coach ? coachT.greenLight : `${colors.primary}14`) : change > 0 ? (coach ? coachT.redLight : `${colors.error}14`) : (coach ? coachT.bg : colors.bgHover),
                        borderRadius: borders.radius.sm,
                        px: 0.8,
                        py: 0.2
                    }}>
                        {change < 0 ? (
                            <TrendingDownIcon sx={{fontSize: 13, color: trendColor}}/>
                        ) : change > 0 ? (
                            <TrendingUpIcon sx={{fontSize: 13, color: trendColor}}/>
                        ) : (
                            <RemoveIcon sx={{fontSize: 13, color: trendColor}}/>
                        )}
                        <Box sx={{fontSize: "11px", fontWeight: 600, color: trendColor}}>
                            {change > 0 ? `+${change.toFixed(1)}` : change.toFixed(1)}
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{fontSize: "11px", color: coach ? coachT.t3 : colors.borderHover}}>-</Box>
                )}
            </Box>
        </Box>
    );
};

/**
 * Why a COMPUTED measurement (נוסחת היקפים) shows no value, in plain Hebrew.
 *
 * The server sends a code rather than prose so the wording lives with the UI. Without this the four
 * very different causes — not configured, no height, no source reading, out of range — all rendered
 * as an identical blank, and telling them apart took a database session each time.
 */
const EMPTY_REASON_TEXT = {
    NOT_MAPPED: 'טרם נבחרו מדידות המקור',
    MISSING_HEIGHT: 'חסר גובה בכרטיס המתאמן',
    MISSING_GENDER: 'חסר מגדר בכרטיס המתאמן',
    NO_SOURCE_VALUES: 'חסרה מדידת היקף בטן או היקף צוואר',
    OUT_OF_RANGE: 'התוצאה חורגת מהטווח הסביר',
};
const emptyReasonText = (measurement) =>
    measurement?.value == null && measurement?.computed
        ? (EMPTY_REASON_TEXT[measurement.emptyReason] || 'אין מספיק נתונים לחישוב')
        : null;

/* ── Coach measurement tile (v2 grid) ────────────────────── */
const CoachMeasurementCard = ({measurement, name, onClick}) => {
    const unit = getUnit(measurement);
    const change = measurement.value != null && measurement.previousValue != null
        ? measurement.value - measurement.previousValue
        : null;
    const trendColor = change == null ? coachT.t3 : change < 0 ? coachT.green : change > 0 ? coachT.red : coachT.t3;
    const lastDate = formatDate(measurement.measurementDate);
    const reason = emptyReasonText(measurement);

    return (
        <Box
            onClick={onClick}
            sx={{
                background: coachT.white,
                border: `1px solid ${coachT.border}`,
                borderRadius: coachT.rs,
                padding: "12px 14px",
                cursor: "pointer",
                transition: "all .14s",
                display: "flex",
                flexDirection: "column",
                gap: "9px",
                "&:hover": {borderColor: coachT.green, boxShadow: "0 2px 10px rgba(15,20,40,.06)", transform: "translateY(-1px)"},
            }}
        >
            {/* Name + last date (date on the left, no extra height) */}
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, minWidth: 0}}>
                <Box sx={{width: 26, height: 26, borderRadius: coachT.rxs, background: coachT.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden"}}>
                    {measurement.imageUrl
                        ? <img src={imageUrl(measurement.imageUrl, 26)} alt={name} style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                        : <StraightenIcon sx={{fontSize: 15, color: coachT.t2}}/>}
                </Box>
                <Box sx={{fontSize: 12, fontWeight: 600, color: coachT.t2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                    {name}
                </Box>
                {/* A computed measurement looks exactly like a measured one once it has a value —
                    same tile, same number. Without this, the first thing anyone tries is to open it
                    and type a correction, which cannot work: the value is derived and the server
                    refuses a write. */}
                {measurement.computed === true && (
                    <MuiTooltip title="מחושב אוטומטית מהיקף הבטן והצוואר — לא ניתן להזנה או לעריכה">
                        <Box sx={{
                            display: "inline-flex", alignItems: "center", gap: "2px",
                            height: 16, px: "5px", borderRadius: coachT.rxs,
                            background: coachT.bg, color: coachT.t3,
                            fontSize: 9.5, fontWeight: 700, flexShrink: 0, whiteSpace: "nowrap",
                        }}>
                            <LockIcon sx={{fontSize: 9}}/>
                            מחושב
                        </Box>
                    </MuiTooltip>
                )}
                <Box sx={{flex: 1}}/>
                {lastDate && (
                    <Box sx={{fontSize: 10, color: coachT.t3, fontWeight: 500, flexShrink: 0, whiteSpace: "nowrap"}}>
                        {lastDate}
                    </Box>
                )}
            </Box>

            {/* Value + trend */}
            <Box sx={{display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 0.75}}>
                <Box sx={{display: "flex", alignItems: "baseline", gap: 0.4, minWidth: 0}}>
                    <Box sx={{fontSize: 22, fontWeight: 800, color: coachT.text, lineHeight: 1, letterSpacing: "-0.5px"}}>
                        {measurement.value != null ? measurement.value.toFixed(1) : "--"}
                    </Box>
                    <Box sx={{fontSize: 11, fontWeight: 600, color: coachT.t3}}>{unit}</Box>
                </Box>
                {change != null && change !== 0 && (
                    <Box sx={{display: "inline-flex", alignItems: "center", gap: "1px", background: change < 0 ? coachT.greenLight : coachT.redLight, color: trendColor, borderRadius: "20px", padding: "2px 7px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap"}}>
                        {change < 0 ? <TrendingDownIcon sx={{fontSize: 12}}/> : <TrendingUpIcon sx={{fontSize: 12}}/>}
                        {change > 0 ? `+${change.toFixed(1)}` : change.toFixed(1)}
                    </Box>
                )}
            </Box>

            {/* Why this computed measurement is blank. An unexplained "--" on a measurement nobody
                can fill in reads as a broken feature. */}
            {reason && (
                <Box sx={{fontSize: 10.5, color: coachT.t3, lineHeight: 1.4, mt: "-3px"}}>
                    {reason}
                </Box>
            )}
        </Box>
    );
};

// --- MeasurementDetailDialog ---

const MeasurementDetailDialog = ({open, onClose, measurement, name, clientId, onDataChanged}) => {
    const navigate = useNavigate();
    const {notificationHandler} = useMessageContext();
    const [chartData, setChartData] = useState([]);
    const [chartLoading, setChartLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingCell, setEditingCell] = useState(null);

    useEffect(() => {
        if (open && measurement) {
            fetchChartData();
        }
    }, [open, measurement]);

    const fetchChartData = async () => {
        setChartLoading(true);
        try {
            const response = await getLast7ByDefinition(clientId, measurement.bodyMeasurementDefinitionId);
            const transformed = (response.data.measurements || [])
                .map(m => ({
                    id: m.id,
                    date: formatDate(m.measurementDate),
                    fullDate: formatFullDate(m.measurementDate),
                    value: m.value,
                    rawDate: parseMeasurementDate(m.measurementDate) || new Date(0),
                    measurementDate: m.measurementDate
                }))
                .sort((a, b) => a.rawDate - b.rawDate);
            setChartData(transformed);
        } catch {
            setChartData([]);
        } finally {
            setChartLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingCell({
            id: record.id,
            value: record.value,
            definitionName: name,
            unit: getUnit(measurement),
            measurementDate: record.measurementDate
        });
        setEditModalOpen(true);
    };

    const handleDelete = async (record) => {
        if (!window.confirm('האם למחוק מדידה זו?')) return;
        try {
            await deleteMeasurement(record.id);
            notificationHandler.success('המדידה נמחקה בהצלחה');
            await fetchChartData();
            onDataChanged?.();
        } catch (err) {
            console.error('Error deleting measurement:', err);
            notificationHandler.error('שגיאה במחיקת המדידה');
        }
    };

    const handleEditSave = () => {
        fetchChartData();
        onDataChanged?.();
    };

    const unit = measurement ? getUnit(measurement) : '';

    const recordsWithChange = useMemo(() => {
        return chartData.map((item, idx) => ({
            ...item,
            change: idx > 0 ? item.value - chartData[idx - 1].value : null
        }));
    }, [chartData]);

    if (!measurement) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: borders.radius.xl,
                    direction: "rtl",
                    overflow: "hidden"
                }
            }}
        >
            {/* Header */}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: borders.default,
                backgroundColor: colors.bg
            }}>
                <Box sx={{fontSize: "18px", fontWeight: 600, color: colors.textPrimary}}>
                    {name}
                </Box>
                <IconButton onClick={onClose} size="small" sx={{color: colors.textSecondary}}>
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </Box>

            <DialogContent sx={{padding: "20px", paddingTop: "20px"}}>
                {/* Chart */}
                {chartLoading ? (
                    <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: 200}}>
                        <CircularProgress sx={{color: colors.primary}}/>
                    </Box>
                ) : (
                    <BodyMeasurementChart data={chartData} unit={unit}/>
                )}

                {/* Recent Records */}
                {recordsWithChange.length > 0 && (
                    <>
                        <Divider sx={{my: 2}}/>
                        <Box sx={{fontSize: "14px", fontWeight: 600, color: colors.textPrimary, mb: 1.5}}>
                            מדידות אחרונות
                        </Box>
                        <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                            {[...recordsWithChange].reverse().map((record, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        padding: "10px 12px",
                                        backgroundColor: colors.bg,
                                        borderRadius: borders.radius.md,
                                        border: `1px solid ${colors.bgHover}`
                                    }}
                                >
                                    {/* Date */}
                                    <Box sx={{fontSize: "13px", color: colors.textSecondary, fontWeight: 500, minWidth: "70px"}}>
                                        {record.fullDate}
                                    </Box>

                                    {/* Value */}
                                    <Box sx={{fontSize: "15px", fontWeight: 600, color: colors.textPrimary}}>
                                        {record.value.toFixed(1)} {unit}
                                    </Box>

                                    {/* Change */}
                                    <Box sx={{
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        minWidth: "60px",
                                        textAlign: "left",
                                        color: record.change == null
                                            ? colors.textMuted
                                            : record.change < 0
                                                ? colors.primary
                                                : record.change > 0
                                                    ? colors.error
                                                    : colors.textMuted
                                    }}>
                                        {record.change != null
                                            ? (record.change > 0 ? `+${record.change.toFixed(1)}` : record.change.toFixed(1))
                                            : '--'
                                        }
                                    </Box>

                                    {/* Actions — a COMPUTED measurement has none. Its history is
                                        derived and has no rows behind it (every `record.id` is
                                        null), so edit would save against nothing and delete would
                                        have nothing to delete. The lock says why they are gone
                                        rather than leaving an unexplained empty column. */}
                                    {measurement?.computed === true ? (
                                        <MuiTooltip title="מחושב אוטומטית מהיקף הבטן והצוואר — לא ניתן לעריכה או למחיקה">
                                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5, color: colors.textMuted, padding: "4px"}}>
                                                <LockIcon sx={{fontSize: 15}}/>
                                            </Box>
                                        </MuiTooltip>
                                    ) : (
                                    <Box sx={{display: "flex", gap: 0.5}}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEdit(record)}
                                            sx={{
                                                color: colors.textMuted,
                                                padding: "4px",
                                                '&:hover': {color: colors.primary, backgroundColor: colors.primarySubtle}
                                            }}
                                        >
                                            <EditIcon sx={{fontSize: 16}}/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(record)}
                                            sx={{
                                                color: colors.textMuted,
                                                padding: "4px",
                                                '&:hover': {color: colors.error, backgroundColor: `${colors.error}14`}
                                            }}
                                        >
                                            <DeleteIcon sx={{fontSize: 16}}/>
                                        </IconButton>
                                    </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                {/* View Full History Button */}
                <Box sx={{display: "flex", justifyContent: "center", mt: 2.5}}>
                    <RTL>
                    <Button
                        variant="outlined"
                        startIcon={<TableChartIcon/>}
                        onClick={() => {
                            onClose();
                            navigate(`/app/clients/${clientId}/measurements`);
                        }}
                        sx={{
                            borderColor: colors.border,
                            color: colors.textSecondary,
                            textTransform: "none",
                            borderRadius: borders.radius.md,
                            padding: "8px 24px",
                            fontWeight: 500,
                            fontSize: "14px",
                            '&:hover': {
                                borderColor: colors.borderHover,
                                backgroundColor: colors.bg
                            }
                        }}
                    >
                        צפייה בהיסטוריה המלאה
                    </Button>
                    </RTL>
                </Box>
            </DialogContent>

            <EditMeasurementModal
                open={editModalOpen}
                onClose={() => {setEditModalOpen(false); setEditingCell(null);}}
                cell={editingCell}
                onSave={handleEditSave}
                notificationHandler={notificationHandler}
            />
        </Dialog>
    );
};

// --- Main Component ---

const ClientBodyMeasurements = ({clientId, cardVariant = "default"}) => {
    const coach = cardVariant === "coach";
    const navigate = useNavigate();
    const [latestData, setLatestData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [selectedMeasurement, setSelectedMeasurement] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    useEffect(() => {
        if (clientId) {
            fetchLatestMeasurements();
        }
    }, [clientId]);

    const fetchLatestMeasurements = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getLatestMeasurements(clientId);
            // Hide values whose company definition was soft-deleted (active === false),
            // then order by the definition's display order.
            //
            // Also drop definitions the trainee has never actually recorded. The endpoint returns a
            // row for every definition in the trainee's catalog, value included or not, so an
            // untouched catalog rendered a full grid of "--" tiles with nothing to show or chart.
            // This panel is a summary of measurements TAKEN; entering a first value is what the
            // "הוספת מדידה" button is for. A recorded 0 is a real value — compare against null, not
            // falsiness.
            //
            // A COMPUTED measurement (נוסחת היקפים) is the exception to the value-less drop. Nobody
            // can "enter a first value" for it — the admin configured it and expects to see it — so
            // hiding it makes a deliberate setup look broken. It stays, showing why it is empty
            // (`emptyReason`), which is the difference between "not measured yet" and "you mapped
            // the wrong two measurements".
            const measurements = (response.data.measurements || [])
                .filter(m => m.active !== false)
                .filter(m => m.value != null || m.computed === true)
                .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
            setLatestData(measurements);
        } catch (err) {
            console.error('Error fetching latest measurements:', err);
            setError('שגיאה בטעינת הנתונים');
            setLatestData([]);
        } finally {
            setLoading(false);
        }
    };

    const getMeasurementName = (measurement) =>
        measurement.definitionNameHeb || measurement.definitionName || '';

    const handleCardClick = (measurement) => {
        setSelectedMeasurement(measurement);
        setDetailOpen(true);
    };

    const handleDetailClose = () => {
        setDetailOpen(false);
        setSelectedMeasurement(null);
    };

    const handleSaveMeasurements = () => {
        fetchLatestMeasurements();
    };

    return (
        <>
            <ChartCard
                variant={cardVariant}
                title="מעקב מדידות גוף"
                subtitle={coach ? "היקפים ואחוז שומן" : undefined}
                icon={RullerIcon}
                loading={loading}
                error={error}
                showData={latestData.length > 0}
                emptyMessage="אין מדידות זמינות"
                flexBasis="50%"
                contentPadding="20px"
                actionButton={
                    <>
                        <MuiTooltip title="צפייה בטבלה">
                            <IconButton
                                size="small"
                                onClick={() => navigate(`/app/clients/${clientId}/measurements`)}
                                sx={{
                                    color: colors.textSecondary,
                                    '&:hover': {
                                        backgroundColor: colors.primarySubtle,
                                        color: colors.primary
                                    }
                                }}
                            >
                                <TableChartIcon fontSize="small"/>
                            </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="תמונות התקדמות">
                            <IconButton
                                size="small"
                                onClick={() => navigate(`/app/clients/${clientId}/progress-photos`)}
                                sx={{
                                    color: colors.textSecondary,
                                    '&:hover': {
                                        backgroundColor: colors.primarySubtle,
                                        color: colors.primary
                                    }
                                }}
                            >
                                <CollectionsIcon fontSize="small"/>
                            </IconButton>
                        </MuiTooltip>
                        <MuiTooltip title="סרטוני התקדמות">
                            <IconButton
                                size="small"
                                onClick={() => navigate(`/app/clients/${clientId}/progress-videos`)}
                                sx={{
                                    color: colors.textSecondary,
                                    '&:hover': {
                                        backgroundColor: colors.primarySubtle,
                                        color: colors.primary
                                    }
                                }}
                            >
                                <VideoLibraryIcon fontSize="small"/>
                            </IconButton>
                        </MuiTooltip>
                    </>
                }
                headerAction={
                    <Button
                        variant="contained"
                        onClick={() => setModalOpen(true)}
                        sx={{
                            backgroundColor: coach ? coachT.green : colors.primary,
                            color: "white",
                            textTransform: "none",
                            borderRadius: coach ? coachT.rs : borders.radius.md,
                            padding: {xs: "6px 12px", md: "8px 20px"},
                            fontWeight: 500,
                            fontSize: {xs: "12px", md: "14px"},
                            boxShadow: "none",
                            '&:hover': {
                                backgroundColor: coach ? coachT.greenDark : colors.primaryDark,
                                boxShadow: "none"
                            }
                        }}
                    >
                        הוספת מדידה
                    </Button>
                }
            >
                {coach ? (
                    /* Coach: dense grid of compact measurement tiles */
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr 1fr", sm: "repeat(3, 1fr)"},
                        gap: 1,
                    }}>
                        {latestData.map((measurement) => (
                            <CoachMeasurementCard
                                key={measurement.bodyMeasurementDefinitionId}
                                measurement={measurement}
                                name={getMeasurementName(measurement)}
                                onClick={() => handleCardClick(measurement)}
                            />
                        ))}
                    </Box>
                ) : (
                    /* Default: compact table of measurements */
                    <Box sx={{
                        border: borders.default,
                        borderRadius: borders.radius.lg,
                        overflow: "hidden",
                        backgroundColor: colors.white,
                    }}>
                        {latestData.map((measurement, index) => (
                            <MeasurementSummaryRow
                                key={measurement.bodyMeasurementDefinitionId}
                                measurement={measurement}
                                name={getMeasurementName(measurement)}
                                onClick={() => handleCardClick(measurement)}
                                isLast={index === latestData.length - 1}
                            />
                        ))}
                    </Box>
                )}

            </ChartCard>

            {/* Detail Dialog */}
            <MeasurementDetailDialog
                open={detailOpen}
                onClose={handleDetailClose}
                measurement={selectedMeasurement}
                name={selectedMeasurement ? getMeasurementName(selectedMeasurement) : ''}
                clientId={clientId}
                onDataChanged={handleSaveMeasurements}
            />

            {/* Add Measurement Modal */}
            <AddBodyMeasurementModal
                clientId={clientId}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSaveMeasurements}
            />
        </>
    );
};

export default ClientBodyMeasurements;